import { RequestStrategyEnum } from '@shared/api/rest/models';
import { isAxiosError } from 'axios';

export class ApiUtils {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static wrapperApi<T extends (...args: any[]) => Promise<void>>(
    fn: T,
    strategy: RequestStrategyEnum = RequestStrategyEnum.Default,
  ): (...args: Parameters<T>) => Promise<void> {
    let debounceTimeout: NodeJS.Timeout | null = null;
    let currentAbortController: AbortController | null = null;
    const delay = 300;

    let requestQueue: Array<{
      args: Parameters<T>;
      resolve: () => void;
      reject: (error: unknown) => void;
    }> = [];

    const processBatch = async (signal?: AbortSignal) => {
      try {
        for (const { args } of requestQueue) {
          if (signal?.aborted) {
            throw new DOMException('AbortError', 'AbortError');
          }
          await fn(...args, signal);
        }
        requestQueue.forEach(({ resolve }) => resolve());
      } catch (error) {
        if (error === 'AbortError') {
          requestQueue.forEach(({ resolve }) => resolve());
        } else {
          requestQueue.forEach(({ reject }) => reject(error));
        }
      } finally {
        requestQueue = [];
      }
    };

    return (...args: Parameters<T>) => {
      return new Promise<void>((resolve, reject) => {
        if (strategy === RequestStrategyEnum.Debounce) {
          requestQueue.push({ args, resolve, reject });

          if (debounceTimeout) {
            clearTimeout(debounceTimeout);
          }

          debounceTimeout = setTimeout(() => {
            processBatch();
          }, delay);
        } else if (strategy === RequestStrategyEnum.Abort) {
          if (currentAbortController) {
            currentAbortController.abort();
          }

          currentAbortController = new AbortController();
          const { signal } = currentAbortController;

          fn(...args, signal)
            .then(resolve)
            .catch((error) => {
              if (error.name === 'AbortError') {
                resolve();
              } else {
                reject(error);
              }
            });
        } else if (strategy === RequestStrategyEnum.Default) {
          fn(...args)
            .then(resolve)
            .catch(reject);
        }
      });
    };
  }

  static getApiError = (error: unknown) => {
    if (isAxiosError(error)) {
      return error.response?.data?.errorMessage || error.message || 'Unknown error';
    } else if (error instanceof Error) {
      return error.message;
    } else {
      return String(error);
    }
  };
}
